// SearchLieu.js
import React, { Fragment, useState, useEffect, useRef, useCallback } from "react";
import axios from "axios"; // Ensure axios is imported
import AutoCompleteResult from "./AutoCompleteResult";
import FloatingInputLabel from "./FloatingInputLabel";
import { connect } from "react-redux";
import { Spinner } from "react-bootstrap";
import useDebounce from "./useDebounce"; // Adjust the path as needed

const SearchLieu = (props) => {
   const comRef = useRef(null);
   const lieuRef = useRef(null);

   const [isLoading, setIsLoading] = useState(false);
   const [results, setResults] = useState([]);
   const [resultsLieu, setResultsLieu] = useState([]);

   const [query, setQuery] = useState("");
   const [queryLieu, setQueryLieu] = useState("");

   const [comChoice, setComChoice] = useState(null);
   const [comLabel, setComLabel] = useState("");

   // Debounced queries
   const debouncedQuery = useDebounce(query, 300);
   const debouncedQueryLieu = useDebounce(queryLieu, 300);
   // on mount, load saved commune if any
   useEffect(() => {
      const savedCode = localStorage.getItem('comChoice');
      const savedLabel = localStorage.getItem('comChoiceLabel');
      if (savedCode && savedLabel) {
         setComChoice(savedCode);
         setComLabel(savedLabel);
      }
   }, []);

   // Initialize with com if exists
   useEffect(() => {
      const initResult = async () => {
         if (props.userCom && props.userComLabel) {
            try {
               props.loading && props.loading(true); // Check if loading prop exists
               setIsLoading(true);
               const response = await axios.get(
                  `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(props.userComLabel)}&citycode=${encodeURIComponent(props.userCom)}&type=municipality`
               );

               if (response.data?.features?.length) {
                  setResults(response.data.features);
                  const initCom = response.data.features[0];
                  setComChoice(initCom.properties.citycode);
                  setComLabel(initCom.properties.city);
                  props.updateCenter(initCom.geometry.coordinates);
               }
            } catch (error) {
               console.error("Error initializing commune:", error);
            } finally {
               props.loading && props.loading(false); // Check if loading prop exists
               setIsLoading(false);
            }
            if (props.levelTerr.trim() === "com") {
               try {
                  const codesTerr = props?.entityCodeTerr ? JSON.parse(props.entityCodeTerr) : null;
                  const comCode = codesTerr ? codesTerr[0].code : " ";
                  if (comCode) {
                     setComChoice(comCode);
                     await getCommGeom(comCode);
                  }
               } catch (error) {
                  console.error("Error initializing SearchSection:", error);
               }
            } else {
               try {
                  setIsLoading(true);
                  const response = await axios.post("Api/geodata/commune_ref", { search: " " });
                  setResults(response.data.data);
                  console.log("SearchSection, empty query", response.data.data);
               } catch (error) {
                  console.error("Error fetching communes for epci:", error);
               } finally {
                  setIsLoading(false);
               }
            }
         }
      };
      initResult();
   }, [props.userCom, props.userComLabel, props.loading, props.updateCenter]);

   // Fetch commune results when debouncedQuery changes
   useEffect(() => {
      if (debouncedQuery.length >= (props.minCharacters || 3)) {
         getResults(debouncedQuery);
      } else {
         setResults([]);
      }
   }, [debouncedQuery]);
   // Fetch commune geometry
   const getCommGeom = async (codeCom) => {
      const params = { code_com: codeCom };
      try {
         setIsLoading(true);
         const response = await axios.post("/Api/geodata/geom/commune", params);
         if (response.data[0]) {
            props.updateVectorLayer(JSON.parse(response.data[0].json_build_object), "commune");
            props.setCurrentCom(codeCom);
         }
      } catch (error) {
         console.error("Error fetching commune geometry:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch lieu-dit results when debouncedQueryLieu changes
   useEffect(() => {
      if (comChoice && debouncedQueryLieu.length >= (props.minCharacters || 3)) {
         getResultsLieu(debouncedQueryLieu);
      } else {
         setResultsLieu([]);
      }
   }, [debouncedQueryLieu, comChoice]);

   // Handler functions using useCallback to prevent unnecessary re-renders
   const handleChange = useCallback((e) => {
      setQuery(e.target.value);
   }, []);

   const handleResetCom = useCallback(() => {
       // clear persisted commune
      localStorage.removeItem('comChoice');
      localStorage.removeItem('comChoiceLabel');

      setComChoice(null);
      setComLabel("");
      setResultsLieu([]);
      props.displayPropList && props.displayPropList(false); // Check if displayPropList exists
   }, [props]);

   const handleChangeLieu = useCallback((e) => {
      setQueryLieu(e.target.value);
   }, []);

   // Fetch commune results
   const getResults = async (searchQuery) => {
      if (!searchQuery || searchQuery.length < (props.minCharacters || 3)) {
         setResults([]);
         return;
      }
      try {
         setIsLoading(true);
         const response = await axios.post("Api/geodata/commune_ref", { search: searchQuery });
         setResults(response.data.data);
      } catch (error) {
         console.error("Error fetching commune results:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Fetch lieu-dit results
   const getResultsLieu = async (searchQueryLieu) => {
      if (!searchQueryLieu || searchQueryLieu.length < (props.minCharacters || 3)) return;
      try {
         setIsLoading(true);
         const response = await axios.get(`/Api/geodata/lieu_dit/${encodeURIComponent(comChoice)}/${encodeURIComponent(searchQueryLieu)}`);
         setResultsLieu(response.data);
      } catch (error) {
         console.error("Error fetching lieu-dit results:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Select commune result
   const selectResult = async (label, insee) => {
      try {
         setIsLoading(true);
         const response = await axios.get(
            `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(label)}&citycode=${encodeURIComponent(insee)}&type=municipality`
         );
         if (response.data?.features.length) {
            const currentResult = response.data.features[0];
            setComChoice(currentResult.properties.citycode);
            setComLabel(currentResult.properties.city);
            // persist
            localStorage.setItem('comChoice', currentResult.properties.citycode);
            localStorage.setItem('comChoiceLabel', currentResult.properties.city);

            props.updateCenter(currentResult.geometry.coordinates);
            setResults([]);
         }
      } catch (error) {
         console.error("Error selecting commune:", error);
      } finally {
         setIsLoading(false);
      }
   };

   // Select lieu-dit result
   const selectResultLieu = (name) => {
      setQueryLieu(name);
      getGeom(comChoice, name);
   };

   // Fetch geometry for selected lieu-dit
   const getGeom = async (codeCom, name) => {
      const params = {
         code_com: codeCom,
         name: name,
      };
      try {
         setIsLoading(true);
         const response = await axios.post("/Api/geodata/geom/lieudit", params);
         props.updateVectorLayer(response.data.geom);
      } catch (error) {
         console.error("Error fetching geometry for lieu-dit:", error);
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <div className="d-flex flex-column">
         {/* Commune Input Field and Autocomplete */}
         {!comChoice ? (
            <div className="my-2 flex-md-row align-items-md-center w-100">
               <Fragment>
                  <div className="form-group w-100 mb-0 position-relative autocomplete-container">
                     <FloatingInputLabel
                        label="Commune INSEE"
                        className="input-proprioC"
                        onChange={handleChange}
                        value={query}
                        name="comSearchInput"
                     />
                     {isLoading && (
                        <Spinner
                           animation="border"
                           size="sm"
                           className="top-50"
                        />
                     )}
                     <AutoCompleteResult
                        results={results}
                        selectResult={selectResult}
                     />
                  </div>
               </Fragment>
            </div>
         ) : comLabel ? (
            <div className="d-flex mt-3 flex-column flex-md-row align-items-md-center w-100 ">
               <div className="input-group mt-2 bg-std-dark mb-3 mb-md-0 w-100">
                  <input
                     type="text"
                     value={`${comLabel} (${comChoice})`}
                     aria-label="Commune"
                     className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1 floating-input-value"
                     readOnly
                  />
                  <button
                     className="btn btn-link border-0 ms-2"
                     type="button"
                     onClick={handleResetCom}
                     aria-label="Clear"
                  >
                     <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
                  </button>
               </div>
            </div>
         ) : (
            <div className="d-flex mt-3 bg-std-dark align-items-center w-100">
               <input
                  type="text"
                  value={comChoice}
                  aria-label="Propriétaire"
                  className="form-control filter-tools-input text-center input-proprioN border-end-0 flex-grow-1"
                  readOnly
               />
               <button
                  className="btn btn-link border-0 ms-2"
                  type="button"
                  onClick={handleResetCom}
                  aria-label="Clear"
               >
                  <i className="fa fa-chevron-circle-left fa-lg" aria-hidden="true"></i>
               </button>
            </div>
         )}

         {/* Lieu-dit Input Field and Autocomplete */}
         {comChoice && (
            <div className="d-flex flex-column flex-md-row align-items-md-center w-100 mt-2">
               <Fragment>
                  <div className="did-floating-label-content form-group mt-2 mb-md-0 w-100 position-relative autocomplete-container">
                     <input
                        type="text"
                        aria-label="Lieu-dit"
                        className="did-floating-input-m form-control filter-tools-input text-center"
                        placeholder=""
                        ref={lieuRef}
                        value={queryLieu}
                        onChange={handleChangeLieu}
                        // Removed onKeyUp to use debounced input
                        autoComplete="off"
                     />
                     <label className="did-floating-label-m">Lieu-dit</label>
                     {isLoading && (
                        <Spinner
                           animation="border"
                           size="sm"
                           className="top-50"
                        />
                     )}
                     <AutoCompleteResult
                        type="lieu"
                        results={resultsLieu}
                        selectResult={selectResultLieu}
                        maxResults={9}
                     />
                  </div>
               </Fragment>
            </div>
         )}
      </div>
   );
};


const mapStateToProps = (state) => ({
   levelTerr: state.MeReducer?.entity?.level_terr,
   userCom: state.MapReducer.currentCom,
   userComLabel: state.MapReducer.currentComLabel,
   filterToolStat: state.SearchReducer.filterToolStat,
   entityCodeTerr: state.MeReducer.entity.codes_terr,
});

const mapDispatchToProps = (dispatch) => ({
   updateCenter: (coordinates, z = 17) =>
      dispatch({
         type: "UPDATE_CENTER_MAP",
         data: { centerMap: coordinates, zoom: z },
      }),
   updateVectorLayer: (geoJsonData) =>
      dispatch({ type: "UPDATE_VECTOR_LAYER", data: { geoJsonData } }),
   togglePanelTab: (currentTab) =>
      dispatch({ type: "TOGGLE_PANEL_TAB", data: { currentTab } }),
   displayPropList: (display) =>
      dispatch({
         type: "DISPLAY_PROP_LIST_PARCELLES",
         data: { displayPropList: display },
      }),
   loading: (isLoading) =>
      dispatch({
         type: "SET_LOADING",
         data: { isLoading },
      }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchLieu);
